/**
 * Same-origin deploy stamp (`/version.json`) written at image build time.
 *
 * Every deploy bakes a new `buildId` (= `VITE_APP_BUILD_ID`). Nginx serves the
 * file with `no-store`, so open tabs discover a newer release without CM-API.
 */

export type DeployedVersion = {
  buildId: string
}

export function getAppBuildId(): string {
  const raw = import.meta.env.VITE_APP_BUILD_ID
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  return 'dev'
}

export function parseDeployedVersion(json: unknown): DeployedVersion | null {
  if (!json || typeof json !== 'object') return null
  const body = json as Record<string, unknown>
  const raw = body.buildId ?? body.webBuildId ?? body.id
  const buildId = typeof raw === 'string' ? raw.trim() : ''
  if (!buildId) return null
  return { buildId }
}

/** True when the running SPA bundle is behind the hosted `/version.json`. */
export function webDeployNeedsReload(
  currentBuildId: string,
  deployedBuildId: string,
): boolean {
  const current = currentBuildId.trim()
  const deployed = deployedBuildId.trim()
  if (!current || !deployed) return false
  return current !== deployed
}

/** Fail-open: `null` means do not force reload from the deploy stamp. */
export async function fetchDeployedVersion(): Promise<DeployedVersion | null> {
  try {
    const res = await fetch('/version.json', {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
    if (!res.ok) return null
    return parseDeployedVersion(await res.json())
  } catch {
    return null
  }
}

/** Unregister service workers (if any), wipe Cache Storage, then hard reload. */
export async function clearCachesAndReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
  } catch {
    // ignore
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // ignore
  }
  const url = new URL(window.location.href)
  url.searchParams.set('_v', String(Date.now()))
  window.location.replace(url.toString())
}
